/**
 * TT-1230's audio-detector boundaries, in one place because three modules must agree on them.
 *
 * Speech.js measures an activity against these live. ReaderManager.js applies the volume share.
 * services/cursor/signalThresholds.js re-applies the same numbers to TT-1256's stored payload when
 * the next day's session replays a prior activity. If those readings ever disagree, the same
 * activity classifies two ways depending on which day the student came back -- and neither
 * bad-signal cursor mode has an automatic exit, so that is a student latched on evidence the
 * record denies.
 *
 * They lived as private consts in Speech.js and ReaderManager.js, transcribed into signalThresholds
 * and guarded by a test that read the source text and matched the literals. A leaf module both
 * sides import makes the drift impossible instead of merely detected -- signalThresholds is
 * imported by CursorOrchestrator, which ReaderManager imports, so it cannot import either of them
 * back. Values only: no imports, no logic, nothing that could make this a cycle.
 *
 * A retune lands here, once.
 */

// PERCENTAGES, 0-100, not fractions: the underlying per-segment field is record.clippingPercentage.
// A units slip turns the pervasive cut of 40 into 4000%, which no phrase can reach, and the branch
// goes permanently and silently inert.
export const CLIPPED_PHRASE_P90_PCT = 1;   // a phrase's p90 segment shows any clipping beyond a trace
export const PERVASIVE_PHRASE_P90_PCT = 40; // ...or is severely clipped

// isPervasivelyClipped: share of phrases at that severity...
export const PERVASIVE_PHRASE_SHARE = 0.5;

// ...over at least this many phrases. Without a floor, one clipped phrase is 1/1 = 100% and fires
// the "pervasive" verdict, which getActivityAudioMetrics() reaching mid-activity makes routine
// rather than hypothetical -- and volumeOnly and unattributed records keep phrasesWithAudioCount
// below the phrases actually read. The labelled sample cannot speak to that regime: its shortest
// activity was 3 phrases and the branch never fired below 4, so any floor up to 4 leaves the
// measured 10-of-34 catch and 1-of-45 false positives untouched. 3 is the smallest denominator the
// analysis actually observed.
export const PERVASIVE_MIN_PHRASES = 3;

// Seconds. TT-1230 recommends 30s; it was 90s while dead silence was a pure dark launch, which made
// the logged wouldFireAtActivityLevel disagree with the rule the analysis landed on.
export const FLATLINE_MIN_RUN_SEC = 30;

// The share of an activity's segments that must be low-RMS for it to read as low volume. Raises the
// isLowVolume field TT-1256 persists as a verdict; it is not a badness rule of its own.
export const LOW_VOLUME_CHECK_THRESHOLD = 0.90;
